import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/react'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'
import {
  createCustomStyleContext,
  useCustomMultiStyleConfig,
} from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum ExtensionBadgeVariant {
  Blue = 'Blue',
  Gray = 'MangoGray',
  LightGray = 'LightGray',
  White = 'White',
}
const { Badge } = themeColorSectionKeys.shared

const extensionBadgeStyleParts = ['container', 'text'] as const
const configHelper = createMultiStyleConfigHelpers(extensionBadgeStyleParts)

export const ExtensionBadgeStyle = configHelper.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      flexShrink: '0',
      width: 'fit-content',
      borderColor: Badge.Blue.Border,
      border: '2px solid',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '3.25rem',
      backgroundColor: Badge.Blue.Background,
      gap: '.5rem',
      userSelect: 'none',
    },
    text: {
      textStyle: textStyleKeys.Body2_Semibold,
      color: Badge.Blue.FontColor,
    },
  },
  variants: {
    [ExtensionBadgeVariant.Blue]: {
      container: {
        color: Badge.Blue.Border,
        backgroundColor: Badge.Blue.Background,
      },
      text: {
        color: Badge.Blue.FontColor,
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
    [ExtensionBadgeVariant.Gray]: {
      container: {
        color: Badge.Gray.Border,
        backgroundColor: Badge.Gray.Background,
      },
      text: {
        color: Badge.Gray.FontColor,
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
    [ExtensionBadgeVariant.LightGray]: {
      container: {
        color: Badge.LightGray.Border,
        backgroundColor: Badge.LightGray.Background,
      },
      text: {
        color: Badge.LightGray.FontColor,
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
    [ExtensionBadgeVariant.White]: {
      container: {
        color: 'white',
        backgroundColor: 'transparent',
      },
      text: {
        color: 'white',
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        borderRadius: '3.125rem',
        px: '.5rem',
        height: '1.125rem',
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
    [Size.md]: {
      container: {
        borderRadius: '50px',
        px: '.5rem',
        height: '1.5rem',
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
      },
    },
    [Size.lg]: {
      container: {
        borderRadius: '3.125rem',
        px: '.75rem',
        height: '1.75rem',
      },
      text: {
        textStyle: textStyleKeys.Body1_Bold,
      },
    },
    [Size.xl]: {
      container: {
        borderRadius: '3.125rem',
        px: '1.25rem',
        height: '2.5rem',
      },
      text: {
        textStyle: textStyleKeys.H5,
      },
    },
  },
  defaultProps: {
    size: Size.md,
    variant: ExtensionBadgeVariant.Blue,
  },
})

const themeKey = getName({ ExtensionBadgeStyle })
export const useExtensionBadgeStyles = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof extensionBadgeStyleParts>(
    themeKey,
    props,
  )
}

export const useExtensionBadgeStyleConfig = () => {
  createCustomStyleContext<typeof extensionBadgeStyleParts>(themeKey)
}
